import heroBanner from "@/assets/hero-banner.webp";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useContentBlock } from "@/hooks/useContentBlock";
import { CONTENT_KEYS } from "@/services/content.service";

const DEFAULTS = {
  subtitle: "Nova Coleção",
  title: "Compre produtos que transformam vidas",
  body: "Seu pedido apoia o resgate e cuidado de animais. Moda, presentes e acessórios com propósito.",
  cta_label: "Ver Produtos",
  cta_link: "#produtos",
  secondary_label: "Conheça a Marca",
  secondary_link: "/sobre",
};

const HeroBanner = () => {
  const { block, isLoading } = useContentBlock(CONTENT_KEYS.HERO);
  if (block && block.is_active === false) return null;

  const settings = (block?.settings as Record<string, any>) ?? {};
  const subtitle = block?.subtitle || DEFAULTS.subtitle;
  const title = block?.title || DEFAULTS.title;
  const body = block?.body || DEFAULTS.body;
  const ctaLabel = block?.cta_label || DEFAULTS.cta_label;
  const ctaLink = block?.cta_link || DEFAULTS.cta_link;
  const secondaryLabel = settings.secondary_label || DEFAULTS.secondary_label;
  const secondaryLink = settings.secondary_link || DEFAULTS.secondary_link;
  // Evita "flash" da imagem antiga: enquanto carrega, não usamos o fallback estático.
  // Só caímos no heroBanner se o carregamento terminou e não há imagem no CMS.
  const imageSrc = block?.image_url || (isLoading ? undefined : heroBanner);

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0">
        {imageSrc && (
          <img
            src={imageSrc}
            alt={title}
            className="h-full w-full object-cover"
            width={1600}
            height={667}
            fetchPriority="high"
            decoding="async"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-r from-foreground/70 via-foreground/40 to-transparent" />
      </div>
      <div className="container relative flex min-h-[420px] items-center py-16 lg:min-h-[520px]">
        <div className="max-w-lg animate-fade-in space-y-6">
          {subtitle && (
            <span className="inline-block rounded-full bg-accent/90 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent-foreground">
              {subtitle}
            </span>
          )}
          <h1 className="font-display text-4xl font-bold leading-tight text-primary-foreground lg:text-5xl xl:text-6xl">
            {title}
          </h1>
          {body && <p className="text-lg text-primary-foreground/85 lg:text-xl">{body}</p>}
          <div className="flex flex-wrap gap-3">
            {ctaLabel && (
              <Button asChild size="lg" className="gap-2 rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
                {ctaLink?.startsWith("#") ? (
                  <a href={ctaLink}>{ctaLabel} <ArrowRight className="h-4 w-4" /></a>
                ) : (
                  <Link to={ctaLink || "/"}>{ctaLabel} <ArrowRight className="h-4 w-4" /></Link>
                )}
              </Button>
            )}
            {secondaryLabel && (
              <Button asChild size="lg" variant="outline"
                className="rounded-full border-2 border-primary-foreground bg-transparent text-primary-foreground hover:bg-primary-foreground hover:text-foreground">
                <Link to={secondaryLink}>{secondaryLabel}</Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
